import { useState, useEffect } from "react";
import { useI18n } from "../lib/i18n.jsx";
import { db } from "../lib/firebase.js";
import { getUserSpaces, createSpace, installMarketplaceTeam } from "../lib/api.js";
import { getDoc, doc } from "firebase/firestore";
import { navigateTo } from "../App.jsx";

export default function InstallFlow({ teamId, user, onBack }) {
  const { t } = useI18n();
  const [step, setStep] = useState("loading");
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [newSpaceName, setNewSpaceName] = useState("");
  const [status, setStatus] = useState("");
  const [team, setTeam] = useState(null);

  useEffect(() => {
    if (!user) { navigateTo("/?login=1"); return; }
    async function init() {
      const teamDoc = await getDoc(doc(db, "marketplace_teams", teamId));
      if (!teamDoc.exists()) { setStep("error"); return; }
      setTeam({ id: teamDoc.id, ...teamDoc.data() });
      const userSpaces = await getUserSpaces();
      const empty = userSpaces.filter((s) => !s.agentRepo);
      setSpaces(empty);
      setStep(empty.length > 0 ? "pick" : "create");
    }
    init();
  }, [user, teamId]);

  async function handleInstall(spaceId) {
    setStep("installing");
    setStatus(t("installing"));
    try {
      const result = await installMarketplaceTeam({ teamId, spaceId, teamVersion: team.defaultTag });
      setStatus(t("install_success"));
      setStep("done");
    } catch (e) {
      setStatus(e.message || t("install_error"));
      setStep("error");
    }
  }

  async function handleCreateAndInstall() {
    if (!newSpaceName.trim()) return;
    setStep("installing");
    setStatus("Creating workspace...");
    try {
      const spaceId = await createSpace(newSpaceName.trim());
      await handleInstall(spaceId);
    } catch (e) {
      setStatus(e.message || t("install_error"));
      setStep("error");
    }
  }

  if (!user) return null;

  return (
    <div className="install-flow">
      <button className="btn btn-ghost back-btn" onClick={onBack}>← {t("back")}</button>
      <h2>{t("select_space")}</h2>

      {step === "pick" && (
        <div className="space-picker">
          <p>{t("select_space")}</p>
          <div className="space-list">
            {spaces.map((s) => (
              <button key={s.id} className={`space-option ${selectedSpace === s.id ? "active" : ""}`}
                      onClick={() => { setSelectedSpace(s.id); handleInstall(s.id); }}>
                <span className="space-name">{s.name || "Unnamed"}</span>
                <span className="space-id">ID: {s.id.slice(0, 8)}…</span>
              </button>
            ))}
          </div>
          <hr />
          <div className="create-space-form">
            <p>{t("create_space")}</p>
            <input type="text" className="form-input" placeholder={t("space_placeholder")}
                   value={newSpaceName} onChange={(e) => setNewSpaceName(e.target.value)} />
            <button className="btn btn-primary" onClick={handleCreateAndInstall} disabled={!newSpaceName.trim()}>
              {t("create_space")}
            </button>
          </div>
        </div>
      )}

      {step === "create" && (
        <div className="space-picker">
          <p>{t("create_space")}</p>
          <input type="text" className="form-input" placeholder={t("space_placeholder")}
                 value={newSpaceName} onChange={(e) => setNewSpaceName(e.target.value)} />
          <button className="btn btn-primary" onClick={handleCreateAndInstall} disabled={!newSpaceName.trim()}>
            {t("create_space")}
          </button>
        </div>
      )}

      {step === "installing" && (
        <div className="install-status">
          <div className="spinner" />
          <p>{status}</p>
        </div>
      )}

      {step === "done" && (
        <div className="install-status success">
          <div className="check-icon">✓</div>
          <p>{status}</p>
          <button className="btn btn-primary" onClick={() => navigateTo("/")}>{t("back")}</button>
        </div>
      )}

      {step === "error" && (
        <div className="install-status error">
          <div className="error-icon">✕</div>
          <p>{status}</p>
          <button className="btn btn-primary" onClick={() => setStep("pick")}>{t("back")}</button>
        </div>
      )}
    </div>
  );
}
